// src/app/components/admin/performance-source-setting.tsx
//
// Platform section of Global Settings: the GLOBAL Performance data source.
// Flips every client's Performance page between synthetic "fixture" data and
// the live "api" endpoint (GET /analytics/series). The value is stored on the
// backend (app_config) and read at runtime — no frontend env var. Admin-only;
// the backend enforces 403 on write for non-admins.

import { useEffect, useState } from "react";
import { Database, FlaskConical, Check, Loader2 } from "lucide-react";

import {
  fetchPerfSeriesSource,
  updatePerfSeriesSource,
  type SeriesSourceMode,
} from "@/app/services/performance-series/source-setting-api";

interface Choice {
  id: SeriesSourceMode;
  label: string;
  desc: string;
  icon: typeof Database;
}

const CHOICES: Choice[] = [
  {
    id: "fixture",
    label: "Fixture (synthetic)",
    desc: "Renders local synthetic data. Safe default while the live endpoint is being validated.",
    icon: FlaskConical,
  },
  {
    id: "api",
    label: "Live API",
    desc: "Calls the real /analytics/series endpoint so the Performance page shows measured data.",
    icon: Database,
  },
];

export function PerformanceSourceSetting() {
  const [current, setCurrent] = useState<SeriesSourceMode | null>(null);
  const [saving, setSaving] = useState<SeriesSourceMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchPerfSeriesSource()
      .then((mode) => alive && setCurrent(mode))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function choose(mode: SeriesSourceMode) {
    if (mode === current || saving) return;
    setSaving(mode);
    setErr("");
    try {
      const stored = await updatePerfSeriesSource(mode);
      setCurrent(stored);
      setSavedAt(Date.now());
      // Let the Performance data seam re-read the (now-changed) source setting.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("analytics:refresh"));
      }
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Performance data source
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          Controls what powers the Performance page for everyone. Changes take effect on the next load
          (within ~1 minute, or immediately after a manual refresh).
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}
          >
            {err}
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3">
          {CHOICES.map((choice) => {
            const Icon = choice.icon;
            const isCurrent = current === choice.id;
            const isSaving = saving === choice.id;
            return (
              <button
                key={choice.id}
                type="button"
                disabled={loading || !!saving}
                onClick={() => choose(choice.id)}
                className="flex items-start gap-3 rounded-xl border p-4 text-left transition-all"
                style={{
                  cursor: loading || saving ? "default" : "pointer",
                  borderColor: isCurrent ? "var(--alpha-teal)" : "var(--border)",
                  backgroundColor: isCurrent ? "var(--alpha-teal-10)" : "var(--card, transparent)",
                  boxShadow: isCurrent ? "0 0 0 1px var(--alpha-teal)" : "none",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <span
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: isCurrent ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)", color: isCurrent ? "#fff" : "var(--muted-foreground)" }}
                >
                  <Icon className="h-4.5 w-4.5" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
                      {choice.label}
                    </span>
                    {isCurrent && !isSaving && (
                      <span className="inline-flex items-center gap-1" style={{ fontSize: "var(--text-xs)", color: "var(--alpha-teal)", fontWeight: "var(--font-weight-medium)" }}>
                        <Check className="h-3.5 w-3.5" /> Active
                      </span>
                    )}
                    {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--alpha-teal)" }} />}
                  </span>
                  <span className="mt-0.5 block" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                    {choice.desc}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {savedAt > 0 && !saving && !err && (
          <p className="mt-3" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Saved. All clients will use “{current}” for the Performance page.
          </p>
        )}
      </div>
    </div>
  );
}

export default PerformanceSourceSetting;
